/* eslint-disable max-lines -- price sheet page: selectors, the grid, term-column and lock
   wiring, and both bulk-action dialogs orchestrated in one route component */
import { useQuery } from '@apollo/client/react';
import {
  DimeButton,
  DimeCard,
  DimeCardContent,
  DimeCardHeader,
  DimeCardTitle,
  toast,
} from '@nudool/design-system';
import { PaymentTermsGroup } from '@nudool/shared';
import * as Sentry from '@sentry/react';
import { createFileRoute } from '@tanstack/react-router';
import { TagIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { ListEmptyState } from '../../components/ListEmptyState.js';
import { useDeniedTooltip } from '../../lib/access/index.js';
import { businessDayStartInstantOf } from '../../lib/business-day-start-instant-of.js';
import {
  GET_STATE_OPTIONS,
  type GetStateOptionsQueryResult,
  type GetStateOptionsQueryVariables,
} from '../../modules/addresses/operations.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { AdjustSelectionDialog } from '../../modules/price-sheet/adjust-selection-dialog.js';
import { CopySheetDialog } from '../../modules/price-sheet/copy-sheet-dialog.js';
import { PriceSheetCellSource, PriceSheetNodeKind } from '../../modules/price-sheet/operations.js';
import {
  findOwnRuleId,
  resolveRowNote,
} from '../../modules/price-sheet/price-sheet-cell-lookup.js';
import type {
  PriceSheetCellBinding,
  PriceSheetRow,
} from '../../modules/price-sheet/price-sheet-columns.js';
import { PriceSheetNoteDialog } from '../../modules/price-sheet/price-sheet-note-dialog.js';
import {
  PriceSheetSelectors,
  type PriceSheetSelectorsValue,
} from '../../modules/price-sheet/price-sheet-selectors.js';
import { buildPriceSheetRows, PriceSheetTree } from '../../modules/price-sheet/price-sheet-tree.js';
import { TermColumnMenu } from '../../modules/price-sheet/term-column-menu.js';
import { usePriceSheetCommit } from '../../modules/price-sheet/use-price-sheet-commit.js';
import { usePriceSheetTermOptions } from '../../modules/price-sheet/use-price-sheet-term-options.js';
import {
  usePriceSheet,
  type PriceSheetCellKey,
} from '../../modules/price-sheet/use-price-sheet.js';
import { useTermColumns } from '../../modules/price-sheet/use-term-columns.js';
import {
  GET_PRICE_RULE_BRANDS,
  type BrandOption,
  type GetPriceRuleBrandsQueryResult,
  type GetPriceRuleBrandsQueryVariables,
} from '../../modules/pricing-rules/operations.js';

export const Route = createFileRoute('/pricing/')({
  component: PricingPage,
});

function findRowCell(row: PriceSheetRow, key: PriceSheetCellKey) {
  return row.node.cells.find(
    (cell) =>
      cell.amountTypeId === key.amountTypeId &&
      cell.paymentTermsId === key.paymentTermsId &&
      cell.paymentTermsGroupId === key.paymentTermsGroupId,
  );
}

function collectRowsByKey(rows: PriceSheetRow[], keys: ReadonlySet<string>, out: PriceSheetRow[]) {
  for (const row of rows) {
    if (keys.has(row.key)) out.push(row);
    collectRowsByKey(row.children, keys, out);
  }
}

// This is the page itself; the small pure helpers above are colocated on purpose (they exist only
// to serve this component).
// eslint-disable-next-line react-refresh/only-export-components
function PricingPage() {
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('pricingEngine:edit');
  // write-price-rule.ts requires this separate, more restrictive permission whenever a write sets
  // `locked: true` -- deliberately not auto-granted to pricingEngine:edit holders (see
  // LockColumnCellProps.canLock's own doc comment).
  const canLock = permissions.includes('pricingEngine:lock');
  const deniedTooltip = useDeniedTooltip();

  const [selectors, setSelectors] = useState<PriceSheetSelectorsValue>({
    brandId: '',
    stateId: null,
    audience: 'everyone',
    customerId: null,
    effectiveDate: null,
  });
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [copyOpen, setCopyOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [noteRow, setNoteRow] = useState<PriceSheetRow | null>(null);
  const effectiveStartIso = businessDayStartInstantOf(selectors.effectiveDate);

  // GET_PRICE_RULE_BRANDS, not the generic top-level `brands` query -- scoped to the caller's own
  // brand_user assignments under pricingEngine:view, same as routes/pricing-rules/index.tsx. The
  // sheet's own Brand filter needs to know the caller's *accessible* brand count (hide it and
  // auto-select when there's exactly one), which the unscoped query
  // can't answer -- it lists every active brand in the system regardless of the caller's access.
  const { data: brandData } = useQuery<
    GetPriceRuleBrandsQueryResult,
    GetPriceRuleBrandsQueryVariables
  >(GET_PRICE_RULE_BRANDS, { variables: { first: 500 } });
  const brands: BrandOption[] = useMemo(
    () => brandData?.priceRuleBrands.edges.map((edge) => edge.node) ?? [],
    [brandData],
  );

  // Same rule as showBrandFilter in price-sheet-selectors.tsx: with exactly one accessible brand
  // there's nothing to pick, so the sheet selects it automatically rather than making the user
  // find and click a filter that isn't even shown. A render-phase update (matching
  // price-sheet-tree.tsx's own hasSeededExpansion), not a useEffect -- this runs once, the first
  // render brands actually resolves to exactly one, without the extra effect-then-rerender pass.
  const [hasAutoSelectedBrand, setHasAutoSelectedBrand] = useState(false);
  if (!hasAutoSelectedBrand && brands.length === 1) {
    setHasAutoSelectedBrand(true);
    setSelectors((prev) => ({ ...prev, brandId: brands[0].id }));
  }

  const { data: stateData } = useQuery<GetStateOptionsQueryResult, GetStateOptionsQueryVariables>(
    GET_STATE_OPTIONS,
    { variables: { first: 100 } },
  );
  const states = useMemo(
    () => stateData?.stateConsts.edges.map((edge) => edge.node) ?? [],
    [stateData],
  );

  const { codPaymentTermsId, allTerms } = usePriceSheetTermOptions(selectors.brandId);

  const { termColumns, isTermVisible, addTerm, removeTerm, expandGroup, collapseGroup } =
    useTermColumns(selectors.brandId, selectors.stateId, allTerms);

  const priceSheet = usePriceSheet({
    brandId: selectors.brandId,
    stateId: selectors.stateId,
    customerId: selectors.customerId,
    effectiveAt: effectiveStartIso,
  });
  const commit = usePriceSheetCommit(priceSheet, {
    refetchQueries: ['GetPriceSheetEffectiveDates'],
  });
  const rows = useMemo(() => buildPriceSheetRows(priceSheet.roots), [priceSheet.roots]);

  const getCellBinding = useCallback(
    (row: PriceSheetRow, key: PriceSheetCellKey): PriceSheetCellBinding => {
      const cell = findRowCell(row, key);
      return {
        isSaving: commit.isCellSaving(row.path, key),
        error: commit.cellError(row.path, key),
        disabled: !canEdit,
        disabledTooltip: canEdit ? undefined : deniedTooltip('pricingEngine:edit'),
        // PriceSheetCellBinding.commit's contract is void | Promise<void> -- commitCell now
        // resolves a success boolean for callers that need it (handleToggleTermColumn below), but
        // a single cell's own binding has no use for it (isSaving/error are driven by commit's
        // own reactive state, not this return value), so it's awaited and discarded here.
        commit: async (nextAmount) => {
          await commit.commitCell({
            path: row.path,
            key,
            brandId: selectors.brandId,
            productTreeId: row.productTreeId,
            productId: row.node.kind === PriceSheetNodeKind.PRODUCT ? row.node.id : null,
            stateId: selectors.stateId,
            customerId: selectors.customerId,
            effectiveStart: effectiveStartIso,
            priceRuleId: cell?.source === PriceSheetCellSource.SET_HERE ? cell.sourceRuleId : null,
            locked: cell?.locked ?? false,
            amount: nextAmount,
          });
        },
      };
    },
    [commit, canEdit, deniedTooltip, selectors, effectiveStartIso],
  );

  const handleToggleLock = useCallback(
    (row: PriceSheetRow, nextLocked: boolean) => {
      const ownAmounts = row.node.cells
        // A SET_HERE cell always carries a real amount (see CommitLockArgs.existingAmounts'
        // own doc comment) -- the `amount != null` check is here only to satisfy the type
        // system's honest "amount can be null" reading of PriceSheetCell.
        .filter(
          (cell): cell is typeof cell & { amount: number } =>
            cell.source === PriceSheetCellSource.SET_HERE && cell.amount != null,
        )
        .map((cell) => ({
          paymentTermsId: cell.paymentTermsId,
          paymentTermsGroupId: cell.paymentTermsGroupId,
          amountTypeId: cell.amountTypeId,
          amount: cell.amount,
        }));
      const priceRuleId = findOwnRuleId(row.node.cells);

      return commit.commitLock({
        path: row.path,
        brandId: selectors.brandId,
        productTreeId: row.productTreeId,
        productId: row.node.kind === PriceSheetNodeKind.PRODUCT ? row.node.id : null,
        stateId: selectors.stateId,
        customerId: selectors.customerId,
        effectiveStart: effectiveStartIso,
        priceRuleId,
        locked: nextLocked,
        existingAmounts: ownAmounts,
      });
    },
    [commit, selectors, effectiveStartIso],
  );

  const handleToggleTermColumn = useCallback(
    async (term: { id: string }, nextVisible: boolean) => {
      if (nextVisible) {
        addTerm(term.id);
        return;
      }
      // Removing a term column deletes that term's amount row wherever it was overridden --
      // walk the currently-loaded tree and clearPrice every matching cell (revision doc §9/R6).
      // commitCell never throws (its own errors surface via toast + inline cellError), so this
      // tracks its boolean result itself: a locked cell the caller can't clear (or any other
      // per-cell rejection) must keep the column visible instead of removeTerm silently hiding a
      // column some of whose prices are still live server-side.
      async function clearTermEverywhere(nodeRows: PriceSheetRow[]): Promise<boolean> {
        const clearPromises: Promise<boolean>[] = [];

        // Synchronous walk to collect every overridden cell up front, so the clears below fire
        // concurrently instead of one round trip per row in series.
        function collectClears(walkRows: PriceSheetRow[]): void {
          for (const row of walkRows) {
            const cell = row.node.cells.find(
              (cell) =>
                cell.paymentTermsId === term.id && cell.source === PriceSheetCellSource.SET_HERE,
            );
            if (cell) {
              // natural-key upsert for the same slice, same rationale as the bulk dialogs.
              clearPromises.push(
                commit.commitCell({
                  path: row.path,
                  key: {
                    amountTypeId: cell.amountTypeId,
                    paymentTermsId: cell.paymentTermsId,
                    paymentTermsGroupId: cell.paymentTermsGroupId,
                  },
                  brandId: selectors.brandId,
                  productTreeId: row.productTreeId,
                  productId: row.node.kind === PriceSheetNodeKind.PRODUCT ? row.node.id : null,
                  stateId: selectors.stateId,
                  customerId: selectors.customerId,
                  effectiveStart: effectiveStartIso,
                  priceRuleId: cell.sourceRuleId,
                  locked: cell.locked,
                  amount: null,
                }),
              );
            }

            collectClears(row.children);
          }
        }

        collectClears(nodeRows);

        // allSettled (rather than all) so one cell's unexpected rejection doesn't cancel
        // reporting on the rest -- commitCell itself resolves `false` rather than throwing, but
        // this stays defensive against a genuinely unexpected rejection instead of losing the
        // other in-flight clears' results.
        const results = await Promise.allSettled(clearPromises);
        for (const result of results) {
          if (result.status === 'rejected') {
            // WHY: commitCell's own contract is "never throws" (errors surface via toast + inline
            // cellError instead) -- a rejection here means that contract broke, so it's worth
            // surfacing beyond the generic toast below.
            Sentry.captureException(result.reason, {
              extra: { context: 'clearTermEverywhere', termId: term.id },
            });
          }
        }
        return results.every((result) => result.status === 'fulfilled' && result.value === true);
      }

      const allCleared = await clearTermEverywhere(rows);
      if (allCleared) {
        removeTerm(term.id);
      } else {
        toast.error(
          `Some prices for this term couldn't be cleared, so the column is still shown. Fix the errors above and try again.`,
        );
      }
    },
    [addTerm, removeTerm, commit, selectors, effectiveStartIso, rows],
  );

  const selectedRows: PriceSheetRow[] = [];
  collectRowsByKey(rows, selectedKeys, selectedRows);

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base">Price Sheet</DimeCardTitle>
      </DimeCardHeader>

      <DimeCardContent className="px-0">
        <PriceSheetTree
          rows={rows}
          terms={termColumns}
          maxBodyHeight="viewport"
          canEdit={canEdit}
          canLock={canLock}
          codPaymentTermsId={codPaymentTermsId}
          onCommit={getCellBinding}
          onToggleLock={(row, next) => void handleToggleLock(row, next)}
          onEditNote={setNoteRow}
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          isLoading={priceSheet.loading}
          emptyMessage={
            selectors.brandId ? undefined : (
              <ListEmptyState
                className=""
                icon={<TagIcon />}
                title="Select a brand"
                description="Choose a brand above to see its category tree and set prices."
              />
            )
          }
          filters={
            <PriceSheetSelectors brands={brands} value={selectors} onChange={setSelectors} />
          }
          actions={
            selectors.brandId ? (
              <>
                <DimeButton
                  variant="outline"
                  size="sm"
                  disabled={!canEdit}
                  disabledTooltip={canEdit ? undefined : deniedTooltip('pricingEngine:edit')}
                  onClick={() => setCopyOpen(true)}
                >
                  Copy from…
                </DimeButton>
                <DimeButton
                  variant="outline"
                  size="sm"
                  disabled={!canEdit || selectedRows.length === 0}
                  disabledTooltip={canEdit ? undefined : deniedTooltip('pricingEngine:edit')}
                  onClick={() => setAdjustOpen(true)}
                >
                  Adjust selected…
                </DimeButton>
                {canEdit && (
                  <TermColumnMenu
                    terms={allTerms}
                    isTermVisible={isTermVisible}
                    onToggleTerm={(term, nextVisible) =>
                      void handleToggleTermColumn(term, nextVisible)
                    }
                    onToggleNetGroup={(expand) =>
                      (expand ? expandGroup : collapseGroup)(PaymentTermsGroup.NET)
                    }
                  />
                )}
              </>
            ) : undefined
          }
        />
      </DimeCardContent>

      {selectors.brandId && (
        <>
          <CopySheetDialog
            open={copyOpen}
            onOpenChange={setCopyOpen}
            brandId={selectors.brandId}
            states={states}
            destinationStateId={selectors.stateId}
            destinationCustomerId={selectors.customerId}
            destinationEffectiveDate={selectors.effectiveDate}
            onCopied={() => {
              void priceSheet.refetch();
            }}
          />
          <AdjustSelectionDialog
            open={adjustOpen}
            onOpenChange={setAdjustOpen}
            selectedRows={selectedRows}
            termColumns={termColumns}
            codPaymentTermsId={codPaymentTermsId}
            brandId={selectors.brandId}
            stateId={selectors.stateId}
            customerId={selectors.customerId}
            effectiveDate={selectors.effectiveDate}
            onAdjusted={() => {
              void priceSheet.refetch();
            }}
          />
        </>
      )}

      <PriceSheetNoteDialog
        open={noteRow !== null}
        onOpenChange={(open) => {
          if (!open) setNoteRow(null);
        }}
        rowName={noteRow?.node.name ?? ''}
        note={noteRow ? resolveRowNote(noteRow.node.cells) : null}
        onSaved={() => {
          void priceSheet.refetch();
        }}
      />
    </DimeCard>
  );
}
